import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CheckoutAndCompleteRequest,
  CompleteOrderRequest,
  CreateOrderRequest,
} from "../../domain/contracts/offline-sync";
import type {
  KitchenTicketFilters,
  KitchenTicketPagination,
} from "../../domain/repo/get-kitchen-tickets";
import type {
  OrderFilters,
  OrderPagination,
  OrderSort,
} from "../../domain/repo/get-orders";
import type { PaymentMethod } from "../../domain/repo/get-payment-methods";
import type {
  GetStockAlertsOptions,
  StockAlertWithDetails,
} from "../../domain/repo/get-stock-alerts";
import type {
  BranchProductWithDetails,
  Category,
  DeliveryPartner,
  GroupedKitchenTicket,
  KitchenStation,
  KitchenTicketWithDetails,
} from "../../domain/types";
import { listOpenLocalOrdersForBranch } from "../lib/offline-sync/db";
import { findVariantByOptionsFromCache } from "../lib/find-variant-from-cache";
import {
  getCachedLatestShift,
  getCachedOpenShift,
  getOrFetchBranchProductsPage,
  getOrFetchCategories,
  getOrFetchDeliveryPartners,
  getOrFetchPaymentMethods,
  getOrFetchProductOptions,
  getOrFetchProductVariants,
  getOrFetchServiceChargePresets,
  getOrFetchTaxSettings,
  getOrFetchVariantModifiers,
  saveCachedLatestShift,
  saveCachedOpenShift,
  searchOrFetchBranchProducts,
} from "../lib/offline-sync/reference-cache";
import type { SyncInboxStatus } from "./client";
import { posApi } from "./client";

const POS_CATALOG_STALE_TIME_MS = 60 * 60 * 1000;

export const posKeys = {
  all: ["pos"] as const,
  branches: () => ["pos", "branches"] as const,
  orders: () => ["pos", "orders"] as const,
  /** invalidate เฉพาะ `useOrders` / ordersList ของสาขา — ไม่ครอบ orderDetail/orderPayments */
  ordersListQueriesForBranch: (branchId: string) =>
    ["pos", "orders", branchId] as const,
  ordersList: (
    branchId: string,
    filters: OrderFilters,
    pagination: OrderPagination,
    sort: OrderSort,
  ) => ["pos", "orders", branchId, filters, pagination, sort] as const,
  carryOverOpenOrders: (
    branchId: string,
    currentShiftId: string,
    filters: import("../../domain/repo/get-orders").CarryOverOpenOrderFilters,
  ) =>
    ["pos", "orders", "carry-over-open", branchId, currentShiftId, filters] as const,
  orderDetail: (orderId: string) => ["pos", "orders", orderId] as const,
  orderPayments: (orderId: string | null, branchId: string | null) =>
    ["pos", "orders", orderId, "payments", branchId] as const,
  orderRefunds: (orderId: string | null, branchId: string | null) =>
    ["pos", "orders", orderId, "refunds", branchId] as const,
  orderDelivery: (orderId: string | null, branchId: string | null) =>
    ["pos", "orders", orderId, "delivery", branchId] as const,
  branchDevices: (branchId: string) =>
    ["pos", "branches", branchId, "devices"] as const,
  branchUsers: (branchId: string) =>
    ["pos", "branches", branchId, "users"] as const,
  branchPrinters: (branchId: string) =>
    ["pos", "branches", branchId, "printers"] as const,
  branchReceiptInfo: (branchId: string) =>
    ["pos", "branches", branchId, "receipt-info"] as const,
  branchBillSettings: (branchId: string) =>
    ["pos", "branches", branchId, "bill-settings"] as const,
  branchCustomerDisplaySettings: (branchId: string) =>
    ["pos", "branches", branchId, "customer-display-settings"] as const,
  branchTables: (branchId: string) =>
    ["pos", "branches", branchId, "tables"] as const,
  categories: (branchId: string) =>
    ["pos", "branches", branchId, "categories"] as const,
  branchProducts: (branchId: string, categoryId?: string) =>
    ["pos", "branches", branchId, "products", categoryId] as const,
  productSearch: (branchId: string, query: string) =>
    ["pos", "branches", branchId, "products", "search", query] as const,
  productVariants: (productId: string, branchId: string) =>
    ["pos", "products", productId, "variants", branchId] as const,
  kitchen: () => ["pos", "kitchen"] as const,
  salesByPaymentMethod: (
    branchId: string,
    query: import("../../domain/contracts/reports").PosSalesByPaymentMethodQueryDTO,
  ) => ["pos", "reports", "sales-by-payment-method", branchId, query] as const,
  kitchenTickets: (
    branchId: string,
    filters: KitchenTicketFilters,
    pagination: KitchenTicketPagination,
  ) => ["pos", "kitchen", "tickets", branchId, filters, pagination] as const,
  kitchenStations: (branchId: string) =>
    ["pos", "kitchen", "stations", branchId] as const,
  openShift: (branchId: string, deviceId: string) =>
    ["pos", "shifts", "open", branchId, deviceId] as const,
  shifts: (branchId: string) => ["pos", "shifts", branchId] as const,
  shiftsList: (branchId: string, limit: number, skip: number) =>
    ["pos", "shifts", "list", branchId, limit, skip] as const,
  latestShift: (branchId: string) =>
    ["pos", "shifts", "latest", branchId] as const,
  shiftReport: (shiftId: string, branchId: string) =>
    ["pos", "shifts", shiftId, "report", branchId] as const,
  stockAlerts: (branchId: string, options?: GetStockAlertsOptions) =>
    ["pos", "stock-alerts", branchId, options] as const,
  stockAlertsCount: (branchId: string) =>
    ["pos", "stock-alerts", "count", branchId] as const,
  loyaltyPointsPreview: (customerId: string, itemsKey: string) =>
    ["pos", "loyalty", "preview", customerId, itemsKey] as const,
  loyaltyRewards: (customerId: string, affordableOnly: boolean) =>
    ["pos", "loyalty", "rewards", customerId, affordableOnly] as const,
  loyaltyRewardPreview: (
    customerId: string,
    rewardId: string,
    orderId: string,
    itemsKey: string,
  ) => ["pos", "loyalty", "preview-reward", customerId, rewardId, orderId, itemsKey] as const,
  syncQueue: (branchId: string, deviceId?: string, shiftId?: string) =>
    ["pos", "sync", "queue", branchId, deviceId, shiftId] as const,
  syncInbox: (
    branchId: string,
    deviceId?: string,
    shiftId?: string,
    status?: SyncInboxStatus,
    limit?: number,
    offset?: number,
  ) =>
    [
      "pos",
      "sync",
      "inbox",
      branchId,
      deviceId,
      shiftId,
      status,
      limit,
      offset,
    ] as const,
};

export function useUserBranches() {
  return useQuery({
    queryKey: posKeys.branches(),
    queryFn: () => posApi.getUserBranches(),
  });
}

export function useBranchDevices(branchId: string | null) {
  return useQuery({
    queryKey: posKeys.branchDevices(branchId ?? ""),
    queryFn: () => {
      if (!branchId) throw new Error("Branch ID is required");
      return posApi.getBranchDevices(branchId);
    },
    enabled: !!branchId,
  });
}

export function useBranchUsers(branchId: string | null) {
  return useQuery({
    queryKey: posKeys.branchUsers(branchId ?? ""),
    queryFn: () => {
      if (!branchId) throw new Error("Branch ID is required");
      return posApi.getBranchUsers(branchId);
    },
    enabled: !!branchId,
  });
}

export function useDeliveryPartners(branchId: string | null) {
  return useQuery<DeliveryPartner[]>({
    queryKey: ["pos", "branches", branchId ?? "", "delivery-partners"],
    queryFn: async () => {
      if (!branchId) throw new Error("Branch ID is required");
      return await getOrFetchDeliveryPartners<DeliveryPartner>(branchId);
    },
    enabled: !!branchId,
  });
}

export function useBranchTaxSettings(branchId: string | null) {
  return useQuery<{ taxRate: number; taxIncluded: boolean }>({
    queryKey: ["pos", "branches", branchId ?? "", "tax-settings"],
    queryFn: async () => {
      if (!branchId) throw new Error("Branch ID is required");
      return await getOrFetchTaxSettings<{
        taxRate: number;
        taxIncluded: boolean;
      }>(branchId);
    },
    enabled: !!branchId,
  });
}

export function useServiceChargePresets(branchId: string | null) {
  return useQuery<
    Array<{
      id: string;
      name: string;
      calculationMode: string;
      rate: number | null;
      amount: number;
      metadata: unknown;
    }>
  >({
    queryKey: ["pos", "branches", branchId ?? "", "service-charge-presets"],
    queryFn: async () => {
      if (!branchId) throw new Error("Branch ID is required");
      return await getOrFetchServiceChargePresets<{
        id: string;
        name: string;
        calculationMode: string;
        rate: number | null;
        amount: number;
        metadata: unknown;
      }>(branchId);
    },
    enabled: !!branchId,
  });
}

export function useBranchPrinters(branchId: string | null) {
  return useQuery({
    queryKey: posKeys.branchPrinters(branchId ?? ""),
    queryFn: () => {
      if (!branchId) throw new Error("Branch ID is required");
      return posApi.getBranchPrinters(branchId);
    },
    enabled: !!branchId,
  });
}

export function useBranchTables(branchId: string | null) {
  return useQuery({
    queryKey: posKeys.branchTables(branchId ?? ""),
    queryFn: async () => {
      if (!branchId) throw new Error("Branch ID is required");
      const [tables, localOpenOrders] = await Promise.all([
        posApi.getBranchTables(branchId),
        listOpenLocalOrdersForBranch(branchId),
      ]);
      const localOccupiedByTable = new Map(
        localOpenOrders
          .filter((order) => order.selection.tableId)
          .map((order) => [order.selection.tableId as string, order]),
      );

      return tables.map((table) => {
        const localOrder = localOccupiedByTable.get(table.id);
        if (!localOrder) return table;
        return {
          ...table,
          isOccupied: true,
          currentOrderId: table.currentOrderId ?? localOrder.clientOrderId,
        };
      });
    },
    enabled: !!branchId,
  });
}

export function useBranchReceiptInfo(branchId: string | null) {
  return useQuery({
    queryKey: posKeys.branchReceiptInfo(branchId ?? ""),
    queryFn: () => {
      if (!branchId) throw new Error("Branch ID is required");
      return posApi.getBranchReceiptInfo(branchId);
    },
    enabled: !!branchId,
  });
}

export function useBranchBillSettings(branchId: string | null) {
  return useQuery({
    queryKey: posKeys.branchBillSettings(branchId ?? ""),
    queryFn: () => {
      if (!branchId) throw new Error("Branch ID is required");
      return posApi.getBranchBillSettings(branchId);
    },
    enabled: !!branchId,
  });
}

export function useUpdateBranchBillSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      branchId,
      data,
    }: {
      branchId: string;
      data: {
        logoUrl?: string | null;
        brandName?: string | null;
        phone?: string | null;
        footerThankYouLao?: string | null;
        footerThankYouEnglish?: string | null;
        showTaxOnBill?: boolean | null;
      };
    }) => posApi.updateBranchBillSettings(branchId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: posKeys.branchBillSettings(variables.branchId),
      });
      queryClient.invalidateQueries({
        queryKey: posKeys.branchReceiptInfo(variables.branchId),
      });
    },
  });
}

export function useBranchCustomerDisplaySettings(branchId: string | null) {
  return useQuery({
    queryKey: posKeys.branchCustomerDisplaySettings(branchId ?? ""),
    queryFn: () => {
      if (!branchId) throw new Error("Branch ID is required");
      return posApi.getBranchCustomerDisplaySettings(branchId);
    },
    enabled: !!branchId,
  });
}

export function useUpdateBranchCustomerDisplaySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      branchId,
      data,
    }: {
      branchId: string;
      data: {
        paymentQrUrls: string[];
        socialMediaUrls?: {
          whatsapp?: string | null;
          facebook?: string | null;
          instagram?: string | null;
          tiktok?: string | null;
        };
      };
    }) => posApi.updateBranchCustomerDisplaySettings(branchId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: posKeys.branchCustomerDisplaySettings(variables.branchId),
      });
    },
  });
}

export function useCategories(branchId: string | null) {
  return useQuery({
    queryKey: posKeys.categories(branchId ?? ""),
    queryFn: async () => {
      if (!branchId) throw new Error("Branch ID is required");
      return await getOrFetchCategories<Category>(branchId);
    },
    enabled: !!branchId,
  });
}

export function useBranchProducts(
  branchId: string | null,
  categoryId?: string | null,
  limit = 20,
  offset = 0,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [
      ...posKeys.branchProducts(branchId ?? "", categoryId ?? undefined),
      limit,
      offset,
    ],
    queryFn: async () => {
      if (!branchId) throw new Error("Branch ID is required");
      return await getOrFetchBranchProductsPage<BranchProductWithDetails>(
        branchId,
        {
          categoryId,
          limit,
          offset,
        },
      );
    },
    enabled: !!branchId && (options?.enabled ?? true),
  });
}

export function useProductSearch(branchId: string | null, query: string) {
  return useQuery({
    queryKey: posKeys.productSearch(branchId ?? "", query),
    queryFn: async () => {
      if (!branchId) throw new Error("Branch ID is required");
      if (!query || query.trim().length === 0) {
        return Promise.resolve([]);
      }
      return await searchOrFetchBranchProducts<BranchProductWithDetails>(
        branchId,
        query,
      );
    },
    enabled: !!branchId && !!query && query.trim().length > 0,
  });
}

export function useProductVariants(
  productId: string | null,
  branchId: string | null,
) {
  return useQuery({
    queryKey: ["pos", "products", productId, "variants", branchId],
    queryFn: async () => {
      if (!productId || !branchId) {
        throw new Error("Product ID and Branch ID are required");
      }
      return await getOrFetchProductVariants<
        Awaited<ReturnType<typeof posApi.getProductVariants>>[number]
      >(branchId, productId);
    },
    enabled: !!productId && !!branchId,
    staleTime: POS_CATALOG_STALE_TIME_MS,
  });
}

export function useProductOptions(
  productId: string | null,
  branchId: string | null,
) {
  return useQuery({
    queryKey: ["pos", "products", productId, "options", branchId],
    queryFn: async () => {
      if (!productId || !branchId) {
        throw new Error("Product ID and Branch ID are required");
      }
      return await getOrFetchProductOptions<
        Awaited<ReturnType<typeof posApi.getProductOptions>>[number]
      >(branchId, productId);
    },
    enabled: !!productId && !!branchId,
    staleTime: POS_CATALOG_STALE_TIME_MS,
  });
}

export function useVariantModifiers(
  variantId: string | null,
  branchId: string | null,
) {
  return useQuery({
    queryKey: ["pos", "variants", variantId, "modifiers", branchId],
    queryFn: async () => {
      if (!variantId || !branchId) {
        throw new Error("Variant ID and Branch ID are required");
      }
      return await getOrFetchVariantModifiers<
        Awaited<ReturnType<typeof posApi.getVariantModifiers>>[number]
      >(branchId, variantId);
    },
    enabled: !!variantId && !!branchId,
    staleTime: POS_CATALOG_STALE_TIME_MS,
  });
}

export function useFindVariantByOptions(
  productId: string | null,
  branchId: string | null,
  optionValueIds: string[],
  enabled = true,
) {
  const sortedOptionValueIds = [...optionValueIds].sort();

  return useQuery({
    queryKey: [
      "pos",
      "products",
      productId,
      "find-variant",
      branchId,
      sortedOptionValueIds.join(","),
    ],
    queryFn: async () => {
      if (!productId || !branchId) {
        throw new Error("Product ID and Branch ID are required");
      }

      const cachedVariant = await findVariantByOptionsFromCache(
        branchId,
        productId,
        sortedOptionValueIds,
      );
      if (cachedVariant) return cachedVariant;

      return posApi.findVariantByOptions(
        productId,
        branchId,
        sortedOptionValueIds,
      );
    },
    enabled: !!productId && !!branchId && enabled,
    staleTime: POS_CATALOG_STALE_TIME_MS,
    placeholderData: (previousData) => previousData,
  });
}

export function usePrefetchProductConfig(branchId: string | null) {
  const queryClient = useQueryClient();

  return (productId: string) => {
    if (!branchId || !productId) return;

    void queryClient.prefetchQuery({
      queryKey: ["pos", "products", productId, "options", branchId],
      queryFn: () =>
        getOrFetchProductOptions<
          Awaited<ReturnType<typeof posApi.getProductOptions>>[number]
        >(branchId, productId),
      staleTime: POS_CATALOG_STALE_TIME_MS,
    });

    void queryClient.prefetchQuery({
      queryKey: ["pos", "products", productId, "variants", branchId],
      queryFn: () =>
        getOrFetchProductVariants<
          Awaited<ReturnType<typeof posApi.getProductVariants>>[number]
        >(branchId, productId),
      staleTime: POS_CATALOG_STALE_TIME_MS,
    });
  };
}

export function useCreateOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      cartItems,
      selection,
      isDelivery,
      deliveryData,
      customerId,
      couponCode,
      couponApplyToCartIndices,
      couponApplications,
    }: CreateOrderRequest) =>
      posApi.createOrder(
        cartItems,
        selection,
        isDelivery,
        deliveryData,
        customerId,
        couponCode,
        couponApplyToCartIndices,
        couponApplications,
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: posKeys.ordersListQueriesForBranch(
          variables.selection.branchId,
        ),
      });
    },
  });
}

export function useCreateOrderAndComplete() {
  const queryClient = useQueryClient();

  return useMutation({
    retry: false,
    mutationFn: (body: CheckoutAndCompleteRequest) =>
      posApi.createOrderAndComplete(body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: posKeys.ordersListQueriesForBranch(
          variables.selection.branchId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: posKeys.branchTables(variables.selection.branchId),
      });
      queryClient.invalidateQueries({ queryKey: posKeys.kitchen() });
    },
  });
}

export function useOrders(
  branchId: string | null,
  filters: OrderFilters = {},
  pagination: OrderPagination = { limit: 20, offset: 0 },
  sort: OrderSort = { field: "createdAt", order: "desc" },
) {
  return useQuery({
    queryKey: posKeys.ordersList(branchId ?? "", filters, pagination, sort),
    queryFn: () => {
      if (!branchId) throw new Error("Branch ID is required");
      return posApi.getOrders(branchId, filters, pagination, sort);
    },
    enabled: !!branchId,
  });
}

export function useCarryOverOpenOrders(
  branchId: string | null,
  currentShiftId: string | null,
  filters: import("../../domain/repo/get-orders").CarryOverOpenOrderFilters = {},
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: posKeys.carryOverOpenOrders(
      branchId ?? "",
      currentShiftId ?? "",
      filters,
    ),
    queryFn: () => {
      if (!branchId || !currentShiftId) {
        throw new Error("Branch ID and shift ID are required");
      }
      return posApi.getCarryOverOpenOrders(branchId, currentShiftId, filters);
    },
    enabled:
      (options?.enabled ?? true) && !!branchId && !!currentShiftId,
  });
}

export function useOrderDetail(
  orderId: string | null,
  branchId: string | null,
) {
  return useQuery({
    queryKey: posKeys.orderDetail(orderId ?? ""),
    queryFn: () => {
      if (!orderId || !branchId) {
        throw new Error("Order ID and Branch ID are required");
      }
      return posApi.getOrderDetail(orderId, branchId);
    },
    enabled: !!orderId && !!branchId,
  });
}

export function useKitchenTickets(
  branchId: string | null,
  filters: KitchenTicketFilters = {},
  pagination: KitchenTicketPagination = { limit: 50, offset: 0 },
) {
  return useQuery<{
    data: KitchenTicketWithDetails[];
    total: number;
    grouped?: GroupedKitchenTicket[];
  }>({
    queryKey: posKeys.kitchenTickets(branchId ?? "", filters, pagination),
    queryFn: () => {
      if (!branchId) throw new Error("Branch ID is required");
      return posApi.getKitchenTickets(branchId, filters, pagination);
    },
    enabled: !!branchId,
    refetchInterval: 5000, // Auto-refresh every 5 seconds for KDS
  });
}

export function useKitchenTicketsByOrder(
  branchId: string | null,
  orderId: string | null,
) {
  return useKitchenTickets(
    branchId,
    { orderId: orderId ?? undefined },
    { limit: 20, offset: 0 },
  );
}

export function useUpdateKitchenTicketStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticketId,
      status,
      assigneeId,
    }: {
      ticketId: string;
      status: "IN_PROGRESS" | "DONE";
      assigneeId?: string;
    }) => posApi.updateKitchenTicketStatus(ticketId, status, assigneeId),
    onSuccess: () => {
      // Invalidate kitchen tickets queries
      queryClient.invalidateQueries({ queryKey: posKeys.kitchen() });
    },
  });
}

export function useKitchenStations(branchId: string | null) {
  return useQuery<KitchenStation[]>({
    queryKey: posKeys.kitchenStations(branchId ?? ""),
    queryFn: () => {
      if (!branchId) throw new Error("Branch ID is required");
      return posApi.getKitchenStations(branchId);
    },
    enabled: !!branchId,
  });
}

export function useUpdateOrderTable() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      branchId,
      tableId,
    }: {
      orderId: string;
      branchId: string;
      tableId: string | null;
    }) => posApi.updateOrderTable(orderId, branchId, tableId),
    onSuccess: (_, variables) => {
      // Invalidate order detail and orders list
      queryClient.invalidateQueries({
        queryKey: posKeys.orderDetail(variables.orderId),
      });
      queryClient.invalidateQueries({
        queryKey: posKeys.ordersListQueriesForBranch(variables.branchId),
      });
      queryClient.invalidateQueries({
        queryKey: posKeys.branchTables(variables.branchId),
      });
    },
  });
}

export function useMoveOrderItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sourceOrderId,
      branchId,
      targetOrderId,
    }: {
      sourceOrderId: string;
      branchId: string;
      targetOrderId: string;
    }) => posApi.moveOrderItems(sourceOrderId, branchId, targetOrderId),
    onSuccess: (_, variables) => {
      // Invalidate both order details and orders list
      // queryClient.invalidateQueries({
      //   queryKey: posKeys.orderDetail(variables.sourceOrderId),
      // });
      queryClient.invalidateQueries({
        queryKey: posKeys.orderDetail(variables.targetOrderId),
      });
      // queryClient.invalidateQueries({ queryKey: posKeys.orders() });
      // Also invalidate kitchen tickets as items moved might affect tickets
      // queryClient.invalidateQueries({ queryKey: posKeys.kitchen() });
    },
  });
}

export function useVoidOrderItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      branchId,
      orderItemId,
      reason,
    }: {
      orderId: string;
      branchId: string;
      orderItemId: string;
      reason: string;
    }) => posApi.voidOrderItem(orderId, branchId, orderItemId, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: posKeys.orderDetail(variables.orderId),
      });
      queryClient.invalidateQueries({
        queryKey: posKeys.ordersListQueriesForBranch(variables.branchId),
      });
    },
  });
}

export function useAddOrderItems() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      branchId,
      cartItems,
    }: {
      orderId: string;
      branchId: string;
      cartItems: import("../context/CartContext").CartItem[];
    }) => posApi.addOrderItems(orderId, branchId, cartItems),
    onSuccess: (_, variables) => {
      // Invalidate order detail and orders list
      queryClient.invalidateQueries({
        queryKey: posKeys.orderDetail(variables.orderId),
      });
      queryClient.invalidateQueries({
        queryKey: posKeys.ordersListQueriesForBranch(variables.branchId),
      });
      // Also invalidate kitchen tickets as new items might create new tickets
      queryClient.invalidateQueries({ queryKey: posKeys.kitchen() });
    },
  });
}

export function usePaymentMethods(branchId: string | null) {
  return useQuery<PaymentMethod[]>({
    queryKey: ["pos", "payment-methods", branchId],
    queryFn: async () => {
      if (!branchId) throw new Error("Branch ID is required");
      return await getOrFetchPaymentMethods<PaymentMethod>(branchId);
    },
    enabled: !!branchId,
  });
}

export function useSalesByPaymentMethodReport(
  branchId: string | null,
  query: Omit<
    import("../../domain/contracts/reports").PosSalesByPaymentMethodQueryDTO,
    "branchId"
  > | null,
  options?: { enabled?: boolean },
) {
  const fullQuery =
    branchId && query
      ? ({ ...query, branchId } as import("../../domain/contracts/reports").PosSalesByPaymentMethodQueryDTO)
      : null;

  return useQuery({
    queryKey: fullQuery
      ? posKeys.salesByPaymentMethod(branchId, fullQuery)
      : ["pos", "reports", "sales-by-payment-method", "disabled"],
    queryFn: async () => {
      if (!fullQuery) throw new Error("Branch ID and query are required");
      return posApi.getSalesByPaymentMethod(fullQuery);
    },
    enabled: (options?.enabled ?? true) && !!fullQuery,
  });
}

export function useCompleteOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: ({
      orderId,
      branchId,
      payments,
      skipKitchenValidation = false,
      voucherCodes,
      couponCode,
      couponOrderItemIds,
      couponApplications,
      customerId,
      rewardId,
      deviceId,
    }: CompleteOrderRequest) =>
      posApi.completeOrder(
        orderId,
        branchId,
        payments,
        skipKitchenValidation,
        voucherCodes,
        couponCode,
        couponOrderItemIds,
        couponApplications,
        customerId,
        rewardId,
        deviceId,
      ),
    onSuccess: (_, { orderId, branchId }) => {
      queryClient.invalidateQueries({ queryKey: posKeys.orderDetail(orderId) });
      queryClient.invalidateQueries({
        queryKey: posKeys.ordersListQueriesForBranch(branchId),
      });
      queryClient.invalidateQueries({
        queryKey: posKeys.branchTables(branchId),
      });
    },
  });
}

export function useVoidOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      branchId,
      reason,
    }: {
      orderId: string;
      branchId: string;
      reason?: string;
    }) => posApi.voidOrder(orderId, branchId, reason),
    onSuccess: (_, { orderId, branchId }) => {
      queryClient.invalidateQueries({ queryKey: posKeys.orderDetail(orderId) });
      queryClient.invalidateQueries({
        queryKey: posKeys.ordersListQueriesForBranch(branchId),
      });
      queryClient.invalidateQueries({
        queryKey: posKeys.branchTables(branchId),
      });
    },
  });
}

export function useOrderPayments(
  orderId: string | null,
  branchId: string | null,
) {
  return useQuery({
    queryKey: posKeys.orderPayments(orderId, branchId),
    queryFn: () => {
      if (!orderId || !branchId) {
        throw new Error("Order ID and Branch ID are required");
      }
      return posApi.getOrderPayments(orderId, branchId);
    },
    enabled: !!orderId && !!branchId,
  });
}

export function useCreateRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      orderId,
      branchId,
      refundInput,
    }: {
      orderId: string;
      branchId: string;
      refundInput: import("../../domain/types").RefundInput;
    }) => posApi.createRefund(orderId, branchId, refundInput),
    onSuccess: (_, { orderId, branchId }) => {
      queryClient.invalidateQueries({ queryKey: posKeys.orderDetail(orderId) });
      queryClient.invalidateQueries({
        queryKey: posKeys.orderPayments(orderId, branchId),
      });
      queryClient.invalidateQueries({
        queryKey: posKeys.orderRefunds(orderId, branchId),
      });
      queryClient.invalidateQueries({
        queryKey: posKeys.ordersListQueriesForBranch(branchId),
      });
    },
  });
}

export function useApproveRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      refundId,
      branchId,
    }: {
      refundId: string;
      branchId: string;
    }) => posApi.approveRefund(refundId, branchId),
    onSuccess: (refund, variables) => {
      if (refund.orderId) {
        queryClient.invalidateQueries({
          queryKey: posKeys.orderDetail(refund.orderId),
        });
        queryClient.invalidateQueries({
          queryKey: posKeys.orderRefunds(refund.orderId, variables.branchId),
        });
        queryClient.invalidateQueries({
          queryKey: posKeys.ordersListQueriesForBranch(variables.branchId),
        });
      }
    },
  });
}

export function useCompleteRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      refundId,
      branchId,
    }: {
      refundId: string;
      branchId: string;
    }) => posApi.completeRefund(refundId, branchId),
    onSuccess: (refund, variables) => {
      if (refund.orderId) {
        queryClient.invalidateQueries({
          queryKey: posKeys.orderDetail(refund.orderId),
        });
        queryClient.invalidateQueries({
          queryKey: posKeys.orderPayments(refund.orderId, variables.branchId),
        });
        queryClient.invalidateQueries({
          queryKey: posKeys.ordersListQueriesForBranch(variables.branchId),
        });
        // Invalidate branch tables if order was DINE_IN
        queryClient.invalidateQueries({
          queryKey: posKeys.branchTables(variables.branchId),
        });
      }
    },
  });
}

export function useRejectRefund() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      refundId,
      branchId,
      reason,
    }: {
      refundId: string;
      branchId: string;
      reason?: string;
    }) => posApi.rejectRefund(refundId, branchId, reason),
    onSuccess: (refund, variables) => {
      if (refund.orderId) {
        queryClient.invalidateQueries({
          queryKey: posKeys.orderDetail(refund.orderId),
        });
        queryClient.invalidateQueries({
          queryKey: posKeys.orderRefunds(refund.orderId, variables.branchId),
        });
        queryClient.invalidateQueries({
          queryKey: posKeys.ordersListQueriesForBranch(variables.branchId),
        });
      }
    },
  });
}

export function useOrderRefunds(
  orderId: string | null,
  branchId: string | null,
) {
  return useQuery({
    queryKey: posKeys.orderRefunds(orderId, branchId),
    queryFn: () => {
      if (!orderId || !branchId) {
        throw new Error("Order ID and Branch ID are required");
      }
      return posApi.getOrderRefunds(orderId, branchId);
    },
    enabled: !!orderId && !!branchId,
  });
}

export function useOrderDelivery(
  orderId: string | null,
  branchId: string | null,
) {
  return useQuery({
    queryKey: posKeys.orderDelivery(orderId, branchId),
    queryFn: () => {
      if (!orderId || !branchId) {
        throw new Error("Order ID and Branch ID are required");
      }
      return posApi.getOrderDelivery(orderId, branchId);
    },
    enabled: !!orderId && !!branchId,
  });
}

export function useShifts(branchId: string | null) {
  return useQuery({
    queryKey: posKeys.shifts(branchId ?? ""),
    queryFn: () => {
      if (!branchId) {
        throw new Error("Branch ID is required");
      }
      return posApi.getShifts(branchId);
    },
    enabled: !!branchId,
  });
}

export function useShiftsList(
  branchId: string | null,
  page: number,
  pageSize: number,
) {
  const skip = (page - 1) * pageSize;
  return useQuery({
    queryKey: posKeys.shiftsList(branchId ?? "", pageSize, skip),
    queryFn: () => {
      if (!branchId) {
        throw new Error("Branch ID is required");
      }
      return posApi.lookupShifts(branchId, { limit: pageSize, skip });
    },
    enabled: !!branchId,
  });
}

export function useLatestShift(branchId: string | null) {
  return useQuery({
    queryKey: posKeys.latestShift(branchId ?? ""),
    // "always" so the queryFn still runs offline and can fall back to the
    // IndexedDB cache instead of leaving the shift context undefined.
    networkMode: "always",
    meta: { suppressErrorToast: true },
    queryFn: async () => {
      if (!branchId) {
        throw new Error("Branch ID is required");
      }
      type LatestShift = Awaited<ReturnType<typeof posApi.getLatestShift>>;
      try {
        const shift = await posApi.getLatestShift(branchId);
        await saveCachedLatestShift(branchId, shift);
        return shift;
      } catch (error) {
        const cached = await getCachedLatestShift<NonNullable<LatestShift>>(
          branchId,
        );
        if (cached !== undefined) return cached;
        throw error;
      }
    },
    enabled: !!branchId,
  });
}

export function useShiftReport(
  shiftId: string | null,
  branchId: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: posKeys.shiftReport(shiftId ?? "", branchId ?? ""),
    queryFn: () => {
      if (!shiftId || !branchId) {
        throw new Error("Shift ID and Branch ID are required");
      }
      return posApi.getShiftReport(shiftId, branchId);
    },
    enabled: (options?.enabled ?? true) && !!shiftId && !!branchId,
  });
}

export function useOpenShift(branchId: string | null, deviceId: string | null) {
  return useQuery({
    queryKey: posKeys.openShift(branchId ?? "", deviceId ?? ""),
    // "always" so the queryFn still runs offline and can fall back to the
    // IndexedDB cache instead of leaving the shift context undefined (which
    // makes previously-saved offline orders disappear after a reboot).
    networkMode: "always",
    meta: { suppressErrorToast: true },
    queryFn: async () => {
      if (!branchId || !deviceId) {
        throw new Error("Branch ID and Device ID are required");
      }
      type OpenShift = Awaited<ReturnType<typeof posApi.getOpenShift>>;
      try {
        const shift = await posApi.getOpenShift(branchId, deviceId);
        await saveCachedOpenShift(branchId, deviceId, shift);
        return shift;
      } catch (error) {
        const cached = await getCachedOpenShift<NonNullable<OpenShift>>(
          branchId,
          deviceId,
        );
        if (cached !== undefined) return cached;
        throw error;
      }
    },
    enabled: !!branchId && !!deviceId,
  });
}

export function useSyncQueueOverview(
  branchId: string | null,
  deviceId?: string | null,
  shiftId?: string | null,
) {
  return useQuery({
    queryKey: posKeys.syncQueue(
      branchId ?? "",
      deviceId ?? undefined,
      shiftId ?? undefined,
    ),
    queryFn: () => {
      if (!branchId) throw new Error("Branch ID is required");
      return posApi.getSyncQueueOverview({
        branchId,
        deviceId: deviceId ?? undefined,
        shiftId: shiftId ?? undefined,
      });
    },
    enabled: !!branchId,
    refetchInterval: 10_000,
  });
}

export function useSyncInboxOrders(params: {
  branchId: string | null;
  deviceId?: string | null;
  shiftId?: string | null;
  status?: SyncInboxStatus;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: posKeys.syncInbox(
      params.branchId ?? "",
      params.deviceId ?? undefined,
      params.shiftId ?? undefined,
      params.status,
      params.limit,
      params.offset,
    ),
    queryFn: () => {
      if (!params.branchId) throw new Error("Branch ID is required");
      return posApi.listSyncInboxOrders({
        branchId: params.branchId,
        deviceId: params.deviceId ?? undefined,
        shiftId: params.shiftId ?? undefined,
        status: params.status,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
      });
    },
    enabled: !!params.branchId,
    refetchInterval: 10_000,
  });
}

export function useRetrySyncInboxOrderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inboxId: string) => posApi.retrySyncInboxOrder(inboxId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pos", "sync"] });
    },
  });
}

export function useResolveSyncInboxOrderMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ inboxId, reason }: { inboxId: string; reason: string }) =>
      posApi.resolveSyncInboxOrder(inboxId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pos", "sync"] });
    },
  });
}

export function useOpenShiftMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      branchId,
      deviceId,
      openingFloat,
    }: {
      branchId: string;
      deviceId: string;
      openingFloat: number;
    }) => posApi.openShift(branchId, deviceId, openingFloat),
    onSuccess: (_, variables) => {
      // Invalidate open shift query
      queryClient.invalidateQueries({
        queryKey: posKeys.openShift(variables.branchId, variables.deviceId),
      });
    },
  });
}

export function useCloseShiftMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      shiftId,
      branchId,
      closingAmount,
      notes,
    }: {
      shiftId: string;
      branchId: string;
      closingAmount: number;
      notes?: string;
    }) => posApi.closeShift(shiftId, branchId, closingAmount, notes),
    onSuccess: () => {
      // Invalidate open shift query (shift is now closed)
      // Need to invalidate all open shift queries for this branch
      queryClient.invalidateQueries({
        queryKey: ["pos", "shifts", "open"],
      });
    },
  });
}

export function useRecalculateShiftMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      shiftId,
      branchId,
    }: {
      shiftId: string;
      branchId: string;
    }) => posApi.recalculateShift(shiftId, branchId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["pos", "shifts", "list", variables.branchId],
      });
    },
  });
}

export function useStockAlerts(
  branchId: string | null,
  options: GetStockAlertsOptions = {},
) {
  return useQuery<StockAlertWithDetails[]>({
    queryKey: posKeys.stockAlerts(branchId ?? "", options),
    queryFn: () => {
      if (!branchId) throw new Error("Branch ID is required");
      return posApi.getStockAlerts(branchId, options);
    },
    enabled: !!branchId,
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });
}

export function useStockAlertsCount(branchId: string | null) {
  return useQuery<{ total: number; outOfStock: number; lowStock: number }>({
    queryKey: posKeys.stockAlertsCount(branchId ?? ""),
    queryFn: () => {
      if (!branchId) throw new Error("Branch ID is required");
      return posApi.getStockAlertsCount(branchId);
    },
    enabled: !!branchId,
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });
}

export function useAcknowledgeStockAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (alertId: string) => posApi.acknowledgeStockAlert(alertId),
    onSuccess: () => {
      // Invalidate stock alerts queries
      queryClient.invalidateQueries({ queryKey: ["pos", "stock-alerts"] });
    },
  });
}

export function useFindOrCreateCustomerByPhone() {
  return useMutation({
    mutationFn: (phone: string) => posApi.findOrCreateCustomerByPhone(phone),
  });
}

export function useEligibleCoupons(
  customerId: string | null | undefined,
  orderType?: string,
  orderSubtotal?: number,
) {
  return useQuery({
    queryKey: [
      "pos",
      "coupons",
      "eligible",
      customerId,
      orderType,
      orderSubtotal,
    ],
    queryFn: () =>
      posApi.getEligibleCoupons(
        customerId ?? undefined,
        orderType,
        orderSubtotal,
      ),
    enabled: customerId !== null && customerId !== undefined,
    staleTime: 30_000,
  });
}

export function useValidateCoupon() {
  return useMutation({
    mutationFn: ({
      code,
      customerId,
      orderSubtotal,
      selectedSubtotal,
      baseAmount,
    }: {
      code: string;
      customerId?: string;
      orderSubtotal?: number;
      selectedSubtotal?: number;
      /** ยอดฐานรวมภาษีสำหรับคำนวณส่วนลด */
      baseAmount?: number;
    }) =>
      posApi.validateCoupon(
        code,
        customerId,
        orderSubtotal,
        selectedSubtotal,
        baseAmount,
      ),
  });
}

export function useValidateVoucher() {
  return useMutation({
    mutationFn: ({ code, customerId }: { code: string; customerId?: string }) =>
      posApi.validateVoucher(code, customerId),
  });
}

export function useLoyaltyPointsPreview(
  customerId: string | null | undefined,
  items: Array<{ variantId: string; quantity: number; lineSubtotal: number }>,
  enabled = true,
) {
  const itemsKey = JSON.stringify(
    items.map((item) => ({
      v: item.variantId,
      q: item.quantity,
      s: Math.round(item.lineSubtotal * 100) / 100,
    })),
  );

  return useQuery({
    queryKey: posKeys.loyaltyPointsPreview(customerId ?? "", itemsKey),
    queryFn: () =>
      posApi.previewLoyaltyPoints({
        customerId: customerId!,
        items,
      }),
    enabled: enabled && !!customerId && items.length > 0,
    staleTime: 10_000,
  });
}

export function useEligibleLoyaltyRewards(
  customerId: string | null | undefined,
  enabled = true,
  affordableOnly = true,
) {
  return useQuery({
    queryKey: posKeys.loyaltyRewards(customerId ?? "", affordableOnly),
    queryFn: () => posApi.getEligibleLoyaltyRewards(customerId!, affordableOnly),
    enabled: enabled && !!customerId,
    staleTime: 30_000,
  });
}

export function usePreviewLoyaltyReward(
  customerId: string | null | undefined,
  rewardId: string | null,
  orderId: string | null | undefined,
  cartItems: Array<{
    variantId?: string | null;
    quantity?: number;
    lineSubtotal?: number;
  }>,
  enabled = true,
) {
  const itemsKey = JSON.stringify(
    cartItems.map((item) => ({
      v: item.variantId ?? "",
      q: item.quantity ?? 0,
      s: Math.round(Number(item.lineSubtotal ?? 0) * 100) / 100,
    })),
  );

  return useQuery({
    queryKey: posKeys.loyaltyRewardPreview(
      customerId ?? "",
      rewardId ?? "",
      orderId ?? "",
      itemsKey,
    ),
    queryFn: () =>
      posApi.previewLoyaltyReward({
        customerId: customerId!,
        rewardId: rewardId!,
        orderId: orderId ?? undefined,
        cartItems,
      }),
    enabled: enabled && !!customerId && !!rewardId && !!orderId,
    staleTime: 5_000,
  });
}
